import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogoIcon } from '@/components/ui/Logo';
import {
  GoogleSignInButton,
  useGoogleAuthErrorMessage,
} from '@/components/auth/GoogleSignInButton';
import {
  Loader2,
  AlertCircle,
  CheckCircle,
  Rocket,
  Sparkles,
  Mail,
  Lock,
  User,
  ArrowRight,
  Gift,
} from 'lucide-react';

function extractNameFromEmail(email: string): string {
  const localPart = email.split('@')[0] || '';
  return (
    localPart
      .replace(/[._-]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim() || 'User'
  );
}

function generateTenantName(name: string): string {
  return `${name}'s Workspace`;
}

export default function Register() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const register = useAuthStore((state) => state.register);
  const googleErrorMessage = useGoogleAuthErrorMessage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Capture referral code from ?ref= query param
  const [referralCode, setReferralCode] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setReferralCode(ref);

    // A failed Google round-trip can bounce back here with ?google_error=<code>.
    const googleError = params.get('google_error');
    if (googleError) {
      setError(googleErrorMessage(googleError));
      params.delete('google_error');
      const qs = params.toString();
      window.history.replaceState(null, '', `/register${qs ? `?${qs}` : ''}`);
    }
    // Read once on mount — the URL is scrubbed immediately after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const passwordStrong = password.length >= 8;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const finalName = name.trim() || extractNameFromEmail(email);
      const tenantName = generateTenantName(finalName);
      await register(email, password, finalName, tenantName, referralCode ?? undefined);
      navigate({ to: '/dashboard', search: { onboarding: 'true' } });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth:register.errors.emailInUse'));
    } finally {
      setIsLoading(false);
    }
  };

  const charsNeeded = 8 - password.length;

  return (
    <div className="min-h-screen w-full flex bg-[var(--color-base)] text-[var(--color-text)]">
      {/* ── Left: form pane ── */}
      <div className="relative w-full lg:w-[480px] flex flex-col p-8 lg:p-12 z-10 bg-[var(--color-base)] shadow-[20px_0_40px_rgba(0,0,0,0.3)]">
        <button
          onClick={() => navigate({ to: '/' })}
          className="flex items-center gap-1.5 self-start"
          aria-label={t('a11y:backHome')}
        >
          <LogoIcon size="lg" />
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <span className="font-logo text-2xl leading-none">my360</span>
        </button>

        <div className="flex-1 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
          >
            <h1 className="text-3xl font-semibold mb-2 tracking-tight">
              {t('auth:register.createAccount')}
            </h1>
            <p className="text-[var(--color-text-muted)] mb-6 max-w-sm">
              {t('auth:register.subtitle')}
            </p>

            {/* Benefit pills */}
            <div className="flex flex-wrap gap-2 mb-8">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10 text-[var(--color-accent)]">
                <CheckCircle className="w-3 h-3" />
                {t('auth:register.freeTours')}
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                <Sparkles className="w-3 h-3" />
                {t('auth:register.noCard')}
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]">
                <Rocket className="w-3 h-3" />
                {t('auth:register.instant')}
              </div>
            </div>

            {/* Provider CTA sits above the form (renders nothing when the
                backend has no Google credentials configured). The ?ref= code
                rides along in the signed OAuth state so a referred signup is
                still attributed. */}
            <GoogleSignInButton
              redirectTo="/dashboard"
              referralCode={referralCode}
              disabled={isLoading}
            />

            <form onSubmit={handleSubmit} className="space-y-4">
              {referralCode && (
                <div className="flex items-center gap-2 rounded-lg border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10 p-3 text-sm text-[var(--color-accent)]">
                  <Gift className="h-4 w-4 flex-shrink-0" />
                  {t('auth:register.referredBy')}{' '}
                  <span className="font-mono font-semibold">{referralCode}</span>
                </div>
              )}
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 p-3 text-sm text-[var(--color-destructive)] animate-shake">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-1">
                <label className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-widest pl-1">
                  {t('auth:register.name')}
                </label>
                <Input
                  type="text"
                  placeholder={t('auth:register.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  icon={<User className="w-4 h-4" />}
                  autoComplete="name"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-widest pl-1">
                  {t('auth:register.email')}
                </label>
                <Input
                  type="email"
                  placeholder={t('auth:register.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  icon={<Mail className="w-4 h-4" />}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-widest pl-1">
                  {t('auth:register.password')}
                </label>
                <Input
                  type="password"
                  placeholder={t('auth:register.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  icon={<Lock className="w-4 h-4" />}
                  required
                  autoComplete="new-password"
                />
                {password.length > 0 && (
                  <p
                    className={`text-[11px] pl-1 mt-1 transition-colors ${
                      passwordStrong ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-muted)]'
                    }`}
                  >
                    {passwordStrong
                      ? t('auth:register.passwordStrong')
                      : t('auth:register.passwordWeak', { count: charsNeeded })}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                disabled={isLoading || !passwordStrong}
                className="w-full h-11 mt-6"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth:register.creating')}
                  </>
                ) : (
                  <>
                    {t('auth:register.createAccountBtn')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </form>

            <p className="text-sm text-[var(--color-text-muted)] text-center mt-8">
              {t('auth:register.alreadyHaveAccount')}{' '}
              <button
                onClick={() => navigate({ to: '/login' })}
                className="text-[var(--color-text)] hover:text-[var(--color-primary)] transition-colors font-medium"
              >
                {t('auth:register.signInLink')}
              </button>
            </p>
          </motion.div>
        </div>
      </div>

      {/* ── Right: ambient pane ── */}
      <div className="hidden lg:block relative flex-1 bg-[var(--color-surface)] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2670&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover opacity-30 animate-[pulse_10s_ease-in-out_infinite]"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[var(--color-base)] opacity-50" />

        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 100, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full pointer-events-none"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 150, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[var(--color-primary)]/10 rounded-full pointer-events-none"
        />

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 glass-panel p-6 rounded-2xl max-w-sm ml-20">
          <div className="flex items-center gap-2 mb-4 text-[var(--color-accent)]">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs uppercase tracking-widest font-semibold">
              {t('auth:register.readyIn60')}
            </span>
          </div>
          <p className="text-xl text-white/90 mb-4 leading-relaxed font-light">
            "{t('auth:register.testimonialQuote')}"
          </p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-accent)]" />
            <div>
              <div className="text-sm font-medium text-white">{t('auth:register.testimonialName')}</div>
              <div className="text-xs text-white/50">{t('auth:register.testimonialRole')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
